import { useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

import { formatDateTime } from '../utils/dateFormat';
const CATEGORY_BADGE = { BUG: 'danger', IDEA: 'primary', COMPLAINT: 'warning', OTHER: 'secondary', CONTACT: 'info' };
const STATUS_BADGE = { NEW: 'warning', IN_PROGRESS: 'primary', RESOLVED: 'info', REOPEN: 'danger', CLOSED: 'success' };
const STATUS_LABEL = { NEW: 'New', IN_PROGRESS: 'In Progress', RESOLVED: 'Resolved', REOPEN: 'Reopen', CLOSED: 'Closed' };

const authorLabel = (msg) => {
  if (msg.authorType === 'ADMIN') return msg.internal ? `Admin (internal) · ${msg.authorName || ''}` : `Admin · ${msg.authorName || ''}`;
  if (msg.authorType === 'SYSTEM') return 'System';
  return `User · ${msg.authorName || ''}`;
};

/**
 * Support-ticket inbox for a logged-in Account Manager - same data shape and behavior as System
 * Admin's Feedback tab, but backed by /api/account-manager/feedback, which the server already
 * restricts to the organizations this Account Manager is currently assigned to (see
 * PartnerAssignmentService). They see the full thread, including internal notes, same as System
 * Admin - nothing is hidden here.
 */
function AccountManagerTickets() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('ALL');
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ status: 'NEW', response: '', adminNotes: '', notifyUser: true });
  const [saving, setSaving] = useState(false);
  const [accountManagerName, setAccountManagerName] = useState('');

  useEffect(() => {
    axios.get('/api/account-manager/organizations')
      .then((r) => setAccountManagerName(r.data?.accountManagerName || ''))
      .catch(() => {});
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/account-manager/feedback');
      setItems(response.data || []);
    } catch (error) {
      toast.error('Error fetching tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const openTicket = async (item) => {
    try {
      const response = await axios.get(`/api/account-manager/feedback/${item.id}`);
      const detail = response.data || item;
      setSelected(detail);
      setForm({ status: detail.status || 'NEW', response: '', adminNotes: '', notifyUser: true });
    } catch (error) {
      toast.error('Failed to load ticket thread');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!selected?.id) return;
    try {
      setSaving(true);
      const payload = { status: form.status, notifyUser: form.notifyUser };
      if (form.response.trim()) payload.response = form.response.trim();
      if (form.adminNotes.trim()) payload.adminNotes = form.adminNotes.trim();
      const response = await axios.put(`/api/account-manager/feedback/${selected.id}`, payload);
      toast.success('Ticket updated');
      setSelected(response.data);
      setForm({ status: response.data?.status || form.status, response: '', adminNotes: '', notifyUser: true });
      await fetchTickets();
    } catch (error) {
      const msg = error.response?.data;
      toast.error(typeof msg === 'string' ? msg : 'Failed to update ticket');
    } finally {
      setSaving(false);
    }
  };

  const filtered = items.filter((item) => (filter === 'ALL' ? true : item.status === filter));

  return (
    <>
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3">
              <h5 className="mb-0">Support tickets{accountManagerName ? ` — ${accountManagerName}` : ''}</h5>
              <Form.Select style={{ width: 'auto' }} value={filter} onChange={(e) => setFilter(e.target.value)}>
                <option value="ALL">All statuses</option>
                <option value="NEW">New</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="RESOLVED">Resolved</option>
                <option value="REOPEN">Reopen</option>
                <option value="CLOSED">Closed</option>
              </Form.Select>
            </div>
            <Button variant="outline-secondary" size="sm" onClick={fetchTickets}>Refresh</Button>
          </div>
          <p className="text-muted small mb-0 mt-2">
            Tickets from the organizations you're currently the Account Manager for.
          </p>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="d-flex justify-content-center"><Spinner animation="border" /></div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Category</th>
                  <th>From</th>
                  <th>Organization</th>
                  <th>Message</th>
                  <th>Reply</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={8} className="text-center text-muted">No tickets from your assigned organizations.</td></tr>
                ) : (
                  filtered.map((item) => (
                    <tr key={item.id}>
                      <td className="text-nowrap">{item.createdAt ? formatDateTime(item.createdAt) : '-'}</td>
                      <td><Badge bg={CATEGORY_BADGE[item.category] || 'secondary'}>{item.category}</Badge></td>
                      <td>
                        <div>{item.userName || '-'}</div>
                        <small className="text-muted">{item.userEmail}</small>
                      </td>
                      <td>{item.organizationName || '-'}</td>
                      <td style={{ maxWidth: 220 }}>{item.message && item.message.length > 100 ? `${item.message.slice(0, 100)}…` : item.message}</td>
                      <td>{item.hasResponse || item.response ? <Badge bg="success">Replied</Badge> : <Badge bg="secondary">Pending</Badge>}</td>
                      <td><Badge bg={STATUS_BADGE[item.status] || 'secondary'}>{STATUS_LABEL[item.status] || item.status}</Badge></td>
                      <td><Button size="sm" variant="outline-primary" onClick={() => openTicket(item)}>View / Respond</Button></td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={!!selected} onHide={() => setSelected(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Ticket #{selected?.id}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            {selected && (
              <>
                <Row className="mb-3">
                  <Col md={6}>
                    <div className="text-muted small">From</div>
                    <div>{selected.userName} ({selected.userEmail})</div>
                  </Col>
                  <Col md={6}>
                    <div className="text-muted small">Organization</div>
                    <div>{selected.organizationName || '-'}</div>
                  </Col>
                </Row>
                <div className="mb-3">
                  <Badge bg={CATEGORY_BADGE[selected.category] || 'secondary'} className="me-2">{selected.category}</Badge>
                  <Badge bg={STATUS_BADGE[selected.status] || 'secondary'} className="me-2">{STATUS_LABEL[selected.status] || selected.status}</Badge>
                  <small className="text-muted">Opened {selected.createdAt ? formatDateTime(selected.createdAt) : ''}</small>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    disabled={selected.status === 'CLOSED'}
                  >
                    <option value={selected.status}>{STATUS_LABEL[selected.status] || selected.status} (current)</option>
                    {(selected.allowedNextStatuses || [])
                      .filter((s) => s !== selected.status)
                      .map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Flow: New → In Progress → Resolved. Only the submitter can Close or Reopen.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Add reply</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={form.response}
                    onChange={(e) => setForm({ ...form, response: e.target.value })}
                    maxLength={5000}
                    placeholder="New message appended to the conversation (leave blank to only update status)"
                    disabled={selected.status === 'CLOSED'}
                  />
                  <Form.Text className="text-muted">{form.response.length}/5000 · Does not overwrite previous replies</Form.Text>
                </Form.Group>

                <Form.Check
                  type="checkbox"
                  id="am-notify-user"
                  className="mb-3"
                  label="Email the customer when a new reply is added"
                  checked={form.notifyUser}
                  onChange={(e) => setForm({ ...form, notifyUser: e.target.checked })}
                  disabled={selected.status === 'CLOSED'}
                />

                <Form.Group className="mb-3">
                  <Form.Label>Add internal note</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={form.adminNotes}
                    onChange={(e) => setForm({ ...form, adminNotes: e.target.value })}
                    placeholder="Visible to System Admin and other Account Managers on this ticket"
                    disabled={selected.status === 'CLOSED'}
                  />
                </Form.Group>

                <div className="border-top pt-3 mt-2">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Conversation</strong>
                    <small className="text-muted">Oldest first</small>
                  </div>
                  <div style={{ maxHeight: 320, overflowY: 'auto', background: 'var(--bs-tertiary-bg, #f8f9fa)', borderRadius: 6, padding: 12 }}>
                    {(selected.messages || []).length === 0 ? (
                      <div className="text-muted small">No messages yet.</div>
                    ) : (
                      selected.messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`mb-2 p-2 rounded border ${msg.internal ? 'border-warning-subtle' : msg.authorType === 'ADMIN' ? 'border-primary-subtle' : 'border-secondary-subtle'}`}
                          style={{
                            background: msg.internal
                              ? 'rgba(255, 193, 7, 0.12)'
                              : msg.authorType === 'ADMIN'
                                ? 'rgba(13, 110, 253, 0.08)'
                                : msg.authorType === 'SYSTEM' ? 'transparent' : 'var(--bs-body-bg)'
                          }}
                        >
                          <div className="d-flex justify-content-between gap-2 mb-1">
                            <small className="fw-semibold">
                              {authorLabel(msg)}
                              {msg.internal && <Badge bg="warning" text="dark" className="ms-2">Internal</Badge>}
                            </small>
                            <small className="text-muted text-nowrap">{msg.createdAt ? formatDateTime(msg.createdAt) : ''}</small>
                          </div>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{msg.body}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setSelected(null)}>Close</Button>
            <Button
              variant="primary"
              type="submit"
              disabled={saving || selected?.status === 'CLOSED' || (!form.response.trim() && !form.adminNotes.trim())}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}

export default AccountManagerTickets;
